import type { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { isOfType } from '@seen/core/utils'
import type { ReactNode } from 'react'
import { Link } from '#src/components/core/Link.tsx'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { useCreatedBy } from '#src/hooks/useCreatedBy.ts'
import { useCreatedOn } from '#src/hooks/useCreatedOn.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useWarningReporter } from '#src/hooks/useWarningReporter.ts'
import { TimelineTextEntry } from '#src/pages/participants/timeline/TimelineTextEntry.tsx'

interface OrderItemProps {
  order: Order
}

export function OrderItem({ order }: OrderItemProps): ReactNode {
  const { data: participant } = useRead(Participant, order.participant)

  const { data: createdBy, isLoading: createdByIsLoading } = useCreatedBy(
    order.reference
  )
  const { data: createdOn, isLoading: createdOnIsLoading } = useCreatedOn(
    order.reference
  )

  useWarningReporter(!order.date && `${order.reference.reference} missing date`)

  const actor = !isOfType(createdBy, 'Practitioner')
    ? order.signedBy
    : createdBy

  return (
    <TimelineTextEntry
      actor={actor ?? createdBy ?? 'Unknown'}
      actorIsLoading={createdByIsLoading}
      time={order.date ?? createdOn ?? null}
      timeIsLoading={!order.date && createdOnIsLoading}
      action={
        <>
          ordered{' '}
          <Link
            to={`/Patient/${participant?.id}/orders/${order.id}`}
            className="font-medium text-foreground decoration-1 underline-offset-[2.5px] hover:underline"
          >
            <span className="relative top-[-0.03125rem] mr-0.5 inline-flex">
              <span className="h-[1em]">
                <IconOrderStatus
                  status={order.status}
                  isSubmitting={order.isSubmitting}
                />
              </span>
            </span>
            {order.titleWithId}
          </Link>
        </>
      }
      reference={order.reference}
      originTask={order.queuedEditsOriginTask}
    />
  )
}
